<template>
  <div class="card mb-4 cursor-pointer" @click="toggleExpanded">
    <div class="flex flex-row flex-wrap sm:flex-nowrap items-stretch sm:items-center p-2 sm:p-3 gap-2 min-w-0">
      <div class="flex flex-col flex-1 min-w-0 order-1">
        <div class="flex flex-col min-w-0">
          <h3 class="text-base sm:text-lg truncate">{{ meeting.otherMember.name }}</h3>
          <span class="text-sm sm:text-md text-[var(--color-disabled)]">
             {{ meetingDate }}
          </span>
        </div>
      </div>
      <div class="flex items-start">
        <font-awesome-icon
          v-if="!expanded"
          icon="fa-solid fa-chevron-down"
          class="text-gray-400 hover:text-gray-600 transition-colors" />
        <font-awesome-icon
          v-else
          icon="fa-solid fa-chevron-up"
          class="text-gray-400 hover:text-gray-600 transition-colors" />
      </div>
      <div class="flex flex-col sm:ml-4 flex-1 min-w-0 items-start sm:items-center order-3 sm:order-2 w-full sm:w-auto basis-full sm:basis-auto">
        <div class="flex items-center mt-0 sm:mt-2 flex-wrap gap-1">
          <span class="font-medium text-sm sm:text-base">{{ $t('TravelBuddyHistory.status') }}:</span>
          <span class="text-sm sm:text-base capitalize">{{ getStatusText }}</span>
        </div>
        <Button v-if="showButton" type="button"
          @click.stop.prevent="markMeeting(meeting.meetingStatus)"
          class="btn-primary-accent mt-1"
          unstyled>
          {{ getButtonText }}
        </Button>
      </div>
    </div>
    <div v-show="expanded" class="flex flex-col border-t pt-4">
      <div v-if="meeting.meetingStatus.toLowerCase() !== 'completed'" class="px-2 sm:px-0">
        <div class="flex min-h-fit w-full justify-center items-center">
          <i class="text-sm sm:text-base"> {{ $t('TravelBuddy.planMeeting') }}</i>
        </div>
        <h4 class="pl-2 sm:pl-6 my-2 text-base sm:text-lg">Agenda</h4>
        <div class="w-full pl-2 sm:pl-6">
          <div class="flex flex-row w-full min-h-5 mb-2 text-sm sm:text-base">
            <span>{{ $t('TravelBuddy.meetingArrival', {text: featureName}) }}</span>
          </div>
          <div class="flex flex-row w-full min-h-5 mb-2 ml-0 sm:ml-4">
            <ol class="list-decimal pl-4">
              <li class="text-sm sm:text-base">{{ $t('TravelBuddy.meetingQ1') }}</li>
              <li class="text-sm sm:text-base">{{ $t('TravelBuddy.meetingQ2') }}</li>
            </ol>
          </div>
        </div>
      </div>

      <div class="px-4 pb-4">
        <h4 class="font-semibold mb-2">{{ $t('TravelBuddyHistory.yourReflections') }}</h4>
        <div v-if="hasOwnFeedbackContent" class="flex flex-col gap-2">
          <span class="text-sm text-[var(--color-disabled)]">
            {{ $t('TravelBuddyHistory.feedbackTime') }}: {{ formatFeedbackTime(ownFeedbackEntry?.date) }}
          </span>
          <span class="flex items-center gap-2">
            <strong>{{ $t('TravelBuddyHistory.helpfulness') }}:</strong>
            <Rating class="travel-buddy-rating-wrap"
              v-if="meeting.meetingStatus === 'completed' && ownFeedbackEntry?.helpfulnessScore != null"
              :model-value="ownFeedbackEntry.helpfulnessScore"
              readonly
              :cancel="false"
            />
            <span v-else class="text-[var(--color-disabled)]">&#8212;</span>
          </span>
          <div>
            <strong class="block mb-1">{{ ownFeedbackQuestionLabel }}:</strong>
            <span>{{ ownFeedbackEntry?.feedback }}</span>
          </div>
          <div v-if="ownFeedbackEntry?.furtherThoughts?.trim()">
            <strong class="block mb-1">{{ $t('TravelBuddyFeedbackOverlayComponent.furtherThoughtsQuestion') }}:</strong>
            <span>{{ ownFeedbackEntry.furtherThoughts }}</span>
          </div>
          <Button
            type="button"
            @click.stop.prevent="editFeedback"
            class="btn-secondary mt-2 w-fit"
            unstyled
          >
            {{ $t('TravelBuddyHistory.editFeedback') }}
          </Button>
        </div>
        <span v-else>{{ $t('TravelBuddyHistory.noFeedbackYet') }}</span>
      </div>

      <div v-if="hasSharedPartnerFeedback" class="px-4 pb-4 border-t pt-4">
        <h4 class="font-semibold mb-2">{{ $t('TravelBuddyHistory.partnerFeedbackTitle', {text: featureName}) }}</h4>
        <div class="flex flex-col gap-2">
          <span class="text-sm text-[var(--color-disabled)]">
            {{ $t('TravelBuddyHistory.feedbackTime') }}: {{ formatFeedbackTime(partnerFeedbackEntry?.date) }}
          </span>
          <span class="flex items-center gap-2">
            <strong>{{ $t('TravelBuddyHistory.helpfulness') }}:</strong>
            <Rating class="travel-buddy-rating-wrap"
              v-if="partnerFeedbackEntry?.helpfulnessScore != null"
              :model-value="partnerFeedbackEntry.helpfulnessScore"
              readonly
              :cancel="false"
            />
            <span v-else class="text-[var(--color-disabled)]">&#8212;</span>
          </span>
          <div>
            <strong class="block mb-1">{{ partnerFeedbackQuestionLabel }}:</strong>
            <span>{{ partnerFeedbackEntry?.feedback }}</span>
          </div>
          <div v-if="partnerFeedbackEntry?.furtherThoughts?.trim()">
            <strong class="block mb-1">{{ $t('TravelBuddyFeedbackOverlayComponent.furtherThoughtsQuestion') }}:</strong>
            <span>{{ partnerFeedbackEntry.furtherThoughts }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
  <TravelBuddyFeedbackOverlayComponent :visible="showDialog"
    @update:visible="handleDialogClose"></TravelBuddyFeedbackOverlayComponent>
</template>

<script>
import { Button, Rating } from 'primevue';
import TravelBuddyFeedbackOverlayComponent from '../Shared/FrontendOverlays/TravelBuddyFeedbackOverlayComponent.vue';
import { formatLocaleDate, formatLocaleDateTime } from '@/helpers/formatLocaleDate';
import { getFeatureDisplayNameOrFallback } from '@/utils/featureDisplay';

export default {
  name: 'TravelBuddyHistoryCardComponent',
  components: { Button, Rating, TravelBuddyFeedbackOverlayComponent },
  props: {
    meeting: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      expanded: false,
      showDialog: false,
    };
  },
  methods: {
    toggleExpanded() {
      this.expanded = !this.expanded;
    },
    async markMeeting(meetingStatus) {
      try {
        this.$store.commit('setTravelBuddyEditFeedbackMode', false);
        this.$store.commit('setTravelBuddyMeetingStatus', meetingStatus);
        await this.$store.dispatch('getSelectedTravelBuddyMeetingInfo', {
          meetingId: this.meeting._id,
          meetingStatusOverride: meetingStatus,
        });
        this.showDialog = true;
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Error!',
          detail: '',
          life: 3000,
        });
      }
    },
    async editFeedback() {
      try {
        this.$store.commit('setTravelBuddyEditFeedbackMode', true);
        await this.$store.dispatch('getSelectedTravelBuddyMeetingInfo', {
          meetingId: this.meeting._id,
        });
        this.showDialog = true;
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Error!',
          detail: '',
          life: 3000,
        });
      }
    },
    async handleDialogClose(visible) {
      this.showDialog = visible;
      if (!visible) {
        this.$store.commit('setTravelBuddyEditFeedbackMode', false);
        await this.$store.dispatch('getTravelBuddyHistory');
      }
    },
    hasFeedbackContent(entry) {
      return Boolean(entry?.feedback?.trim?.());
    },
    formatFeedbackTime(dateValue) {
      if (!dateValue) {
        return '-';
      }

      const formatted = formatLocaleDateTime(dateValue);
      return formatted || '-';
    },
  },
  computed: {
    featureName() {
      return getFeatureDisplayNameOrFallback(
        this.$store,
        'travelBuddyHistory',
        this.$t('FallbackTexts.travelBuddyHistory'),
        this.$i18n?.locale,
      );
    },
    ownFeedbackEntry() {
      return this.meeting.meetingFeedback?.find(
        (entry) => String(entry.user) !== String(this.meeting.otherMember._id),
      ) || null;
    },
    partnerFeedbackEntry() {
      return this.meeting.meetingFeedback?.find(
        (entry) => String(entry.user) === String(this.meeting.otherMember._id),
      ) || null;
    },
    hasOwnFeedbackContent() {
      return this.hasFeedbackContent(this.ownFeedbackEntry);
    },
    hasSharedPartnerFeedback() {
      return Boolean(
        this.partnerFeedbackEntry
        && this.partnerFeedbackEntry.shareWithTravelBuddy
        && this.hasFeedbackContent(this.partnerFeedbackEntry),
      );
    },
    ownFeedbackQuestionLabel() {
      if (this.meeting.meetingStatus === 'skipped') {
        return this.$t('TravelBuddyHistory.feedbackContentSkipped');
      }
      return this.$t('TravelBuddyHistory.feedbackContent');
    },
    partnerFeedbackQuestionLabel() {
      return this.$t('TravelBuddyHistory.feedbackContent');
    },
    getStatusText() {
      const status = this.meeting.meetingStatus;
      if(status.toLowerCase() === 'completed') {
        return this.$t('TravelBuddyHistory.completed');
      }
      if(status.toLowerCase() === 'skipped') {
        return this.$t('TravelBuddyHistory.skipped');
      }
      return this.$t('TravelBuddyHistory.created');
    },
    getButtonText() {
      const status = this.meeting.meetingStatus;
      if(status.toLowerCase() === 'completed') {
        return this.$t('TravelBuddyFeedbackOverlayComponent.provideFeedback');
      }
      if(status.toLowerCase() === 'skipped') {
        return this.$t('TravelBuddyHistory.skippedButtonText');
      }
      return this.$t('TravelBuddyHistory.createdButtonText');
    },
    showButton() {
      const status = this.meeting.meetingStatus;
      if(status.toLowerCase() === 'completed' && this.hasOwnFeedbackContent) {
        return false;
      }
      return true;
    },
    meetingDate() {
      return formatLocaleDate(this.meeting.meetingDate);
    },
  },
};
</script>
<style scoped>
.travel-buddy-rating-wrap :deep(.p-rating-icon) {
  color: var(--color-orange);
}

.travel-buddy-rating-wrap :deep(.p-rating-icon.p-rating-icon-active) {
  color: var(--color-orange);
}

.travel-buddy-rating-wrap :deep(.p-rating-icon:hover),
.travel-buddy-rating-wrap :deep(.p-rating-item:hover .p-rating-icon) {
  color: var(--color-orange) !important;
}
</style>
